// CarrierLoginsConfigPage
// This page targets the "CarrierLogins" entity as a standalone route.
// It keeps per-tab isolation and persistence by storing operation and the
// selected row under stable keys in M_State. The CRUD route is dedicated to
// carrier logins.
//
// Notes:
// - Uses the same permissions module as Carriers (roleName="Carriers") so existing roles apply.
// - Table actions open a CRUD tab at /home/carriers/carrierLoginsConfigCrud.
// - Keys stored in M_State are distinct from CarrierConfig to avoid collisions:
//     - "operation"
//     - "CarrierLoginLine"
//
// Layout notes:
// - The outer shell mirrors the previous CarrierPage "logins" section:
//   title area based on MenuCarrierLoginManagementLabel + responsive content area.
// - We also keep the tab label in sync with the same i18n key, so the top tab
//   reflects the backend MenuDisplay value for this sub-menu.

import PropTypes from 'prop-types';
import React, {
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import {
  LoadingContext,
  NavigateContext,
  M_StateActions,
  MTable,
} from 'src/M_Components';

export default function CarrierLoginsConfigPage({ roles, onCreateTab, toastMessages }) {
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(false);
  const matches = useMediaQuery('(min-width:900px)');

  // ------------------------------------------------------------------------------------------------------------
  // Read current selected tab id from global state so we can safely rename the tab caption, if desired.
  // ------------------------------------------------------------------------------------------------------------
  const selectedTab = useSelector(
    (s) => s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value
  );

  // ------------------------------------------------------------------------------------------------------------
  // EFFECT
  // Update tab title when language changes. This keeps the tab label aligned with the menu display:
  //   MenuCarrierLoginManagementLabel (submenu under the Carriers root).
  //
  // IMPORTANT:
  // - This is optional but useful when the user changes language at runtime.
  // - It assumes the tab was initially created using the same menu display key.
  // ------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedTab) return;
    renameTab(String(selectedTab), t('MenuCarrierLoginManagementLabel'));
  }, [selectedTab, renameTab, t]);

  // Memoized array to avoid re-renders
  const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

  // Stable route for CRUD (dedicated to CarrierLogins)
  const CRUD_ROUTE = '/home/carriers/carrierLoginsConfigCrud';

  // ===========================================================================================
  // Opens/renames the tab and saves necessary state - stable between renders
  // ===========================================================================================
  const openCarrierLoginCrud = useCallback(
    (operation, data) => {
      // Save the current operation
      dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

      // For Edit/View/Copy ensure a row is selected
      if (['Edit', 'Copy', 'View'].includes(operation)) {
        if (!data) {
          toastMessages('error', '', t('SelectedLine'));
          return;
        }
        // Persist the selected login line under a specific key to avoid clashing with CarrierConfig
        dispatch(M_StateActions.addM_State({ key: 'CarrierLoginLine', value: data }));
      }

      // Build a null-safe label for the tab
      const safeName = data?.Name ?? '';
      const tabLabel =
        operation === 'Create'
          ? t('LabelNewCarrierLogin')
          : operation === 'View'
          ? `${t('LabelViewCarrierLogin')} "${safeName}"`
          : `${t('LabelEditCarrierLogin')} "${safeName}"`;

      // Open the CRUD tab
      onCreateTab(CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  // ===========================================================================================
  // Memoized replace functions for table actions
  // ===========================================================================================
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ['View', (row) => openCarrierLoginCrud('View', row)],
        ['Create', () => openCarrierLoginCrud('Create')],
        ['Edit', (row) => openCarrierLoginCrud('Edit', row)],
      ]),
    [openCarrierLoginCrud]
  );

  return (
    <div className="divGlobalSettings" style={{ padding: '20px' }}>
      {/* Title area shows the section title based on i18n keys */}
      <div
        className="titleSettings"
        style={{
          color: ctx.secondaryColor,
          padding: '20px',
          marginBottom: '20px',
        }}
      >
        {t('MenuCarrierLoginManagementLabel')}
      </div>

      {/* Keep layout responsive as before. The main content is the CarrierLogins list + CRUD actions. */}
      <div style={matches ? { display: 'flex' } : {}}>
        <div className="settingsPageMainDiv" style={{ width: '100%' }}>
          <div className="divGlobalSettings">
            <div className="subTitleSettings">
              <div>
                <div style={{ color: ctx.secondaryColor }}>{t('CarrierLogins')}</div>
                <div className="labelSubtitleSettings">{t('InfoCarrierLogins')}</div>
              </div>
            </div>

            <MTable
              // Show filters on table header
              filtersOnTable
              // Point to the CarrierLogins entity (server-side resource)
              entityName="CarrierLogins"
              // Keep permissions under the Carriers module so existing roles apply
              roleName="Carriers"
              // Extract permission values for this module
              rolePermissions={roles?.map((vl) => vl.Value)}
              // Remove specified context menu items
              removeContextItems={removeContextItems}
              // Refresh state to trigger data reload
              refresh={refresh}
              // Function to update refresh state
              setRefresh={setRefresh}
              // Disable session storage for this table (children handle their own per-tab persistence)
              useSessionStorage={false}
              // Replace default actions with custom functions
              replaceFunctions={replaceFunctions}
              // Keep consistent layout
              tabsRailWidth={250}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

CarrierLoginsConfigPage.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};
